import { useRuntimeConfig } from '#app'

export interface UnityInstance {
  SendMessage(objectName: string, methodName: string, value: string | number | boolean): void

  Quit(): Promise<void>
}

export interface UnityConfig {
  arguments: any[]
  dataUrl: string
  frameworkUrl: string
  codeUrl: string
  streamingAssetsUrl: string
  companyName: string
  productName: string
  productVersion: string
  showBanner: (msg: any, type: any) => void
  cacheControl: (url: string) => void
}

export interface UnityMessage {
  id: number
  time_stamp: string
  msg: any
}

declare global {
  interface Window {
    unityInstance?: UnityInstance
  }
}

declare function createUnityInstance(
  canvas: HTMLCanvasElement,
  config: UnityConfig,
  onProgress?: (progress: number) => void,
): Promise<UnityInstance>

declare const unityProfiler: {
  createButton(instance: UnityInstance): HTMLElement
  shutDown(): void
}

let editorWebSocket: InstanceType<typeof globalThis.WebSocket> | null = null

function initEditorWebSocket() {
  if (editorWebSocket) return editorWebSocket

  console.log('🔌 Connecting to Unity Editor WebSocket...')

  editorWebSocket = new WebSocket('ws://localhost:8080/unity')

  editorWebSocket.addEventListener('open', () => {
    console.log('✅ WebSocket connected to Unity Editor!')

    const initMessage = {
      name: 'InitParams',
      id: 2,
      time_stamp: new Date().toISOString().split('.')[0] + 'Z',
      msg: {
        platform: 'Web',
        browser: navigator.userAgent,
        environment: 'Development',
      },
    }

    editorWebSocket!.send(JSON.stringify(initMessage))
    console.log('📤 Sent InitParams to Unity:', initMessage)

    window.postMessage({ name: 'UnityEditorReady' }, '*')
  })

  // ✅ Add message listener to receive data FROM Unity
  editorWebSocket.addEventListener('message', event => {
    try {
      const data = JSON.parse(event.data)
      console.log('📥 Received from Unity:', data)

      // Forward Unity messages to the window message system
      // so your app can handle them the same way as WebGL
      window.postMessage(
        {
          name: data.name,
          id: data.id,
          time_stamp: data.time_stamp,
          msg: data.msg,
        },
        '*',
      )
    } catch (error) {
      console.error('❌ Failed to parse Unity message:', error, event.data)
    }
  })

  editorWebSocket.addEventListener('error', error => {
    console.error('❌ WebSocket error:', error)
  })

  editorWebSocket.addEventListener('close', () => {
    console.log('🔌 WebSocket disconnected')
    editorWebSocket = null
  })

  return editorWebSocket
}

function attachUnityProfilerButton(instance: UnityInstance) {
  if (typeof unityProfiler === 'undefined' || !unityProfiler.createButton) {
    console.warn('❌ unityProfiler missing')
    return
  }

  const btn = unityProfiler.createButton(instance)
  btn.style.position = 'fixed'
  btn.style.right = '0px'
  btn.style.top = '50%'
  btn.style.transform = 'translateY(-50%)'
  btn.style.zIndex = '2147483647'
  btn.style.color = 'rgb(0, 0, 0)'

  document.body.appendChild(btn)

  window.addEventListener('beforeunload', () => {
    unityProfiler.shutDown?.()
  })
}

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const s = document.createElement('script')
    s.src = src
    s.onload = () => resolve()
    s.onerror = () => reject(new Error(`Failed to load script: ${src}`))
    document.body.appendChild(s)
  })
}

export default async (canvas: HTMLCanvasElement): Promise<UnityInstance> => {
  const env = useRuntimeConfig()
  const enableUnityProfiler = env.public.USE_UNITY_PROFILER === 'true'

  if (env.public.USE_UNITY_EDITOR === 'true') {
    initEditorWebSocket()

    return {
      SendMessage(objectName: string, methodName: string, value: string | number | boolean) {
        if (editorWebSocket!.readyState === WebSocket.OPEN) {
          let messageData
          try {
            messageData = typeof value === 'string' ? JSON.parse(value) : value
          } catch {
            console.error('Failed to parse message value:', value)
            return
          }

          const payload = {
            name: methodName,
            id: messageData.id || 1,
            time_stamp: messageData.time_stamp || new Date().toISOString(),
            msg: messageData.msg || messageData,
          }

          editorWebSocket!.send(JSON.stringify(payload))
          console.log('📤 Sent to Unity:', methodName, messageData)
        } else {
          console.warn('⚠️ WebSocket not ready, state:', editorWebSocket!.readyState)
        }
      },
      async Quit() {
        editorWebSocket?.close()
      },
    }
  }

  const UNITY_BUILD_URL = `${env.public.UNITY_BUILD_BASE_URL}/${env.public.UNITY_BUILD_VERSION}`
  const UNITY_LOADER_URL = `${UNITY_BUILD_URL}/build.loader.js`
  const UNITY_PROFILER_URL = `${env.public.UNITY_BUILD_BASE_URL}/TemplateData/profiler.js`

  function unityShowBanner(msg: any, type: any) {
    console.log('showBanner', msg, type)
  }

  const config = {
    arguments: [],
    dataUrl: `${UNITY_BUILD_URL}/build.data`,
    frameworkUrl: `${UNITY_BUILD_URL}/build.framework.js`,
    codeUrl: `${UNITY_BUILD_URL}/build.wasm`,
    streamingAssetsUrl: 'StreamingAssets',
    companyName: 'VRIFY',
    productName: '3D viewer',
    productVersion: '0.1.0',
    showBanner: unityShowBanner,
    cacheControl: function (url: string) {
      if (url.match(/\.data/) || url.match(/\.bundle/)) {
        return 'must-revalidate'
      }
      if (url.match(/mapbox.*\.png/)) {
        return 'immutable'
      }
      return 'no-store'
    },
  }

  // Ensure loader exists
  if (!('createUnityInstance' in window)) {
    await loadScript(UNITY_LOADER_URL)
  }

  // Load profiler.js only when profiling enabled
  if (enableUnityProfiler) {
    try {
      await loadScript(UNITY_PROFILER_URL)
    } catch (e) {
      console.warn(`Failed to load Unity profiler script: ${UNITY_PROFILER_URL}`, e)
    }
  }

  const instance = await createUnityInstance(canvas, config)
  window.unityInstance = instance

  if (enableUnityProfiler) {
    attachUnityProfilerButton(instance)
  }

  return instance
}
